/**
 * ModelSelector Component
 * Compact icon trigger that opens an elegant, minimal model picker popover.
 */

import { useState, useRef, useEffect, memo } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { emitJourneyEvent, JOURNEY_EVENTS } from '@modules/journey/detect';
import { findCatalogEntry, refLabel, type LabelCatalogEntry } from '@/utils/modelLabels';
import './ModelSelector.css';

interface Model {
  id: string;
  name: string;
  provider?: string;
  description?: string;
  /** Concrete pinned version behind this family — READ-ONLY display info. */
  resolved_model?: string;
  resolved_model_name?: string;
  version_ids?: string[];
  /** Each release's own name — a selected ref that is a release OTHER than
   *  the pin (a meta-agent obeys one exactly) is named by it. */
  version_names?: Record<string, string>;
  aliases?: string[];
  enabled?: boolean;
}

/**
 * Match a stored model ref against the family catalog.
 *
 * Conversations and bots may still STORE legacy concrete ids (e.g.
 * 'claude-sonnet-4-5-20250929') while the catalog now returns families
 * ('sonnet', 'gpt-mini', …). A ref belongs to a family when it is the
 * family key, the currently pinned release, OR ANY release of that family.
 *
 * That last clause matters: matching only the pinned release made a bot
 * holding a non-pinned version of the same family look "no longer
 * available" — BotModal then deselected it and the next save persisted the
 * narrowed whitelist, quietly losing configuration. `version_ids` comes
 * from the catalog and mirrors the backend's normalize_ref_to_family, so
 * both sides answer this question the same way.
 */
export function findModelEntry<T extends LabelCatalogEntry>(
  models: T[],
  ref: string | null | undefined,
): T | undefined {
  return findCatalogEntry(models, ref);
}

interface ModelSelectorProps {
  models: Model[];
  selectedModel: string;
  onModelChange: (modelId: string) => void;
  disabled?: boolean;
  /** Emit the journey's "model switched" detection. Opt IN, default off, for
   *  the same reason as ChatInput's: this picker also appears in composers
   *  that are not the product being used (see ChatInput's `journeyDetection`). */
  journeyDetection?: boolean;
}

function ModelSelectorImpl({
  models,
  selectedModel,
  onModelChange,
  disabled = false,
  journeyDetection = false,
}: ModelSelectorProps) {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleSelect = (modelId: string) => {
    // Journey detection: only a REAL switch counts — re-picking the current
    // family must not pay for a gesture the user did not make.
    const current = findModelEntry(models, selectedModel);
    if (journeyDetection && (!current || current.id !== modelId)) {
      emitJourneyEvent(JOURNEY_EVENTS.modelSwitched, { modelId });
    }
    onModelChange(modelId);
    setIsOpen(false);
  };

  // Normalize the stored/selected ref (family key OR legacy concrete id)
  // to its catalog family entry so highlighting + the trigger tooltip work
  // for conversations that still store pre-families concrete ids.
  const selectedEntry = findModelEntry(models, selectedModel);
  // Users see the concrete RELEASE that will run ("Claude Sonnet 5"), not the
  // family name: the tenant's pin for a family, or — for a ref naming another
  // release of it, which a meta-agent runs exactly — that release
  // (utils/modelLabels.refLabel). The family is still what gets stored and
  // what the admin pins, so re-pinning a family changes the label here
  // without touching anything the user or the agent has saved.
  const triggerTitle = refLabel(models, selectedModel) || selectedModel;

  return (
    <div className="model-selector" ref={containerRef}>
      <button
        className={`model-selector-trigger ${isOpen ? 'open' : ''}`}
        data-testid="model-selector-trigger"
        onClick={() => !disabled && setIsOpen(o => !o)}
        disabled={disabled}
        type="button"
        aria-label={t('chat.modelPicker') || 'Select model'}
        title={triggerTitle}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 3l1.8 4.6L18 9l-4.2 1.4L12 15l-1.8-4.6L6 9l4.2-1.4L12 3z" />
          <path d="M18 14l.9 2.3L21 17l-2.1.7L18 20l-.9-2.3L15 17l2.1-.7L18 14z" />
        </svg>
      </button>

      {isOpen && (
        <div className="model-selector-popover" role="listbox">
          <div className="model-selector-popover-label">
            {t('chat.modelPickerLabel') || 'Model'}
          </div>
          {models.map(model => {
            const isActive = selectedEntry
              ? selectedEntry.id === model.id
              : selectedModel === model.id;
            return (
              <button
                key={model.id}
                type="button"
                role="option"
                aria-selected={isActive}
                className={`model-selector-option ${isActive ? 'selected' : ''}`}
                onClick={() => handleSelect(model.id)}
                title={isActive ? triggerTitle : (model.resolved_model_name || model.name)}
              >
                <span className="model-option-labels">
                  {/* The pinned RELEASE is the label users read. The family name
                      is deliberately not shown — it is an admin-side concept.
                      Falls back to the family name for a ref the catalog can't
                      resolve, so a row is never blank. */}
                  <span className="model-option-name" dir="auto">
                    {isActive ? triggerTitle : (model.resolved_model_name || model.name)}
                  </span>
                </span>
                {isActive && (
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="model-option-check">
                    <path d="M5 12l5 5L20 7" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Memoized: the picker's props (models / selectedModel / onModelChange /
 * disabled) don't change while the user types in the chat textarea, so it must
 * NOT re-render on every keystroke. Without this, each character re-rendered
 * the whole popover and made fast typing janky / drop characters.
 */
export const ModelSelector = memo(ModelSelectorImpl);
